(() => {
  let restockObserverStarted = false;

  const normalizeText = (element) => {
    if (element instanceof HTMLInputElement) return element.value.trim().toLowerCase();
    return (element.textContent || element.getAttribute('aria-label') || '').trim().toLowerCase();
  };

  const initializeMediaViewer = () => {
    const viewer = document.querySelector('[data-qq-media-viewer]');
    if (!viewer || viewer.dataset.initialized === 'true') return;
    viewer.dataset.initialized = 'true';

    const modal = viewer.closest('#productMediaModal');
    const stage = viewer.querySelector('[data-qq-media-stage]');
    const slides = Array.from(viewer.querySelectorAll('[data-qq-media-slide]'));
    const counter = viewer.querySelector('[data-qq-media-counter]');
    if (!modal || !stage || slides.length === 0) return;

    let activeIndex = 0;
    let touchStartX = null;

    const pauseInactiveMedia = () => {
      slides.forEach((slide, index) => {
        if (index === activeIndex) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };

    const showSlide = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      if (counter) counter.textContent = `${activeIndex + 1} / ${slides.length}`;
      pauseInactiveMedia();
    };

    const showMedia = (mediaId) => {
      const index = slides.findIndex((slide) => slide.dataset.qqMediaSlide === mediaId);
      showSlide(index >= 0 ? index : 0);
    };

    document.querySelectorAll('[data-side-drawer-open][data-qq-media-id]').forEach((trigger) => {
      trigger.addEventListener('click', () => showMedia(trigger.dataset.qqMediaId));
    });

    viewer.querySelector('[data-qq-media-previous]')?.addEventListener('click', () => showSlide(activeIndex - 1));
    viewer.querySelector('[data-qq-media-next]')?.addEventListener('click', () => showSlide(activeIndex + 1));

    stage.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchStartX;
      const distance = endX - touchStartX;
      touchStartX = null;
      if (Math.abs(distance) < 45) return;
      showSlide(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('popup--visible')) return;
      if (event.key === 'ArrowLeft') showSlide(activeIndex - 1);
      if (event.key === 'ArrowRight') showSlide(activeIndex + 1);
    });

    showSlide(0);
  };

  const findRestockControl = () => {
    const selectors = [
      'button',
      'a[role="button"]',
      'input[type="button"]',
      'input[type="submit"]',
      'iframe[title*="notify" i]',
      'iframe[src*="restock" i]',
      'iframe[src*="back-in-stock" i]'
    ];

    return Array.from(document.querySelectorAll(selectors.join(','))).find((element) => {
      if (element.closest('[data-qq-restock-placement]')) return false;
      const text = normalizeText(element);
      return text.includes('notify me when available') || text.includes('notify when available') || text.includes('back in stock alert') || text === 'notify me';
    });
  };

  const placeRestockControl = () => {
    /* Move the Alert Me button (popup mode) under the Sold out button.
       Only the button's own wrapper is moved, never a shared container, and never when an
       inline form is next to it, so the app keeps working. */
    const placement = document.querySelector('[data-qq-restock-placement]');
    if (!placement || placement.classList.contains('has-restock-control')) return false;
    const control = findRestockControl();
    if (!control) return false;

    let node = control;
    const stopAt = control.closest('[product-content-wrapper], .product__info, .product-single__info, main') || document.body;
    while (node.parentElement && node.parentElement !== stopAt && node.parentElement.children.length === 1) {
      node = node.parentElement;
    }
    const scope = node.parentElement || node;
    const hasInlineForm = node.querySelector('input[type="email"]') || (scope !== stopAt && scope.querySelector('input[type="email"]'));
    if (hasInlineForm) return false;

    placement.appendChild(node);
    placement.classList.add('has-restock-control');
    return true;
  };

  /* Alert Me opens its popup outside the product area. After the button is clicked, find the
     newly shown popup (a fixed box mentioning "notify") and tag it so the theme can style it. */
  const tagRestockPopup = () => {
    const isShown = (el) => { const r = el.getBoundingClientRect(); return r.width > 120 && r.height > 60 && getComputedStyle(el).visibility !== 'hidden'; };
    const candidates = [...document.body.querySelectorAll('div, section, aside, dialog, form')].filter((el) => {
      if (el.closest('[data-qq-restock-placement], #MainContent header, .shopify-section-group-header-group')) return false;
      const pos = getComputedStyle(el).position;
      if (pos !== 'fixed' && !(el.tagName === 'DIALOG' && el.open)) return false;
      return isShown(el) && /notify|back in stock|restock|available again/i.test(el.textContent || '');
    });
    candidates.forEach((el) => {
      el.classList.add('qq-bis-layer');
      const box = [...el.querySelectorAll('div, form, section')].find((c) => isShown(c) && getComputedStyle(c).backgroundColor !== 'rgba(0, 0, 0, 0)' && c.getBoundingClientRect().width < window.innerWidth * 0.95);
      (box || el).classList.add('qq-bis-popup');
    });
  };
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-qq-restock-placement]')) return;
    [80, 300, 800, 1500].forEach((t) => window.setTimeout(tagRestockPopup, t));
  });

  const initialize = () => {
    initializeMediaViewer();
    placeRestockControl();

    if (restockObserverStarted) return;
    restockObserverStarted = true;

    const observer = new MutationObserver(() => {
      placeRestockControl();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 20000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('change', (event) => {
    if (!event.target.closest('[product-content-wrapper]')) return;
    window.setTimeout(placeRestockControl, 100);
    window.setTimeout(placeRestockControl, 700);
  });
})();
